import { forwardRef } from 'react'
import type { ButtonHTMLAttributes } from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/lib/utils'

/**
 * Button primitive — o `.btn` do protótipo Lumen (design/lumen/lumen.css §10),
 * mantendo a API shadcn/ui intacta (mesmas variants, mesmos sizes).
 *
 * Vocabulário Lumen:
 *   • cápsula sempre (`rounded-full`), 36px de altura (sm 30px, icon 36×36);
 *   • `default` = o único gradiente do sistema (`--primary-top` → `--primary-base`)
 *     com o glow primário — é a ação com mais peso da tela, e só uma por tela.
 *     Os tokens têm um valor por mundo (globals.css §2/§3): no escuro são a
 *     escala de marca (brand-300 → brand-500); no claro, verdes que levam texto
 *     branco acima de 4,5:1 (#443);
 *   • `secondary` = superfície afundada + fio forte, sem emissão nenhuma;
 *   • `ghost` = transparente até ser tocado.
 *
 * O feedback de pressão é `scale(0.97)`, não translação: o botão afunda sob o
 * dedo em vez de saltar, coerente com o resto do sistema.
 */
const buttonVariants = cva(
  [
    'inline-flex items-center justify-center gap-2 whitespace-nowrap',
    'font-medium select-none cursor-pointer',
    'transition-[transform,box-shadow,background,border-color,color] duration-150 ease-out',
    // Foco SEMPRE visível: o anel segue o raio da cápsula (ao contrário do
    // `outline` global, que reimpõe um raio de 10px e esquadra a pílula). A cor
    // é por mundo: o accent a 60% no escuro, opaco no claro, onde a 60% não
    // chegava aos 3:1 sobre o fundo (#443).
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring',
    'disabled:pointer-events-none disabled:opacity-50',
    'active:scale-[0.97]',
    '[&_svg]:size-4 [&_svg]:shrink-0',
  ].join(' '),
  {
    variants: {
      variant: {
        default: [
          'text-accent-fg font-semibold border-0',
          'bg-[linear-gradient(180deg,var(--primary-top),var(--primary-base))]',
          'shadow-[var(--shadow-primary-glow)]',
          'hover:bg-[linear-gradient(180deg,var(--primary-top-hover),var(--primary-base-hover))]',
          'hover:shadow-[var(--shadow-primary-glow-hover)]',
        ].join(' '),
        secondary: [
          'bg-sunk text-ink border border-hair-strong',
          'hover:bg-panel-hover hover:border-ink-4',
        ].join(' '),
        outline: [
          'border border-hair-strong text-ink bg-transparent',
          'hover:bg-sunk hover:border-ink-4',
        ].join(' '),
        ghost: 'bg-transparent border border-transparent text-ink-2 hover:bg-sunk hover:text-ink',
        link: 'text-accent underline-offset-4 hover:underline',
        destructive: 'bg-error text-white hover:opacity-90',
      },
      size: {
        sm: 'h-[30px] px-3 text-[12.5px] rounded-full',
        default: 'h-9 px-4 text-[13.5px] rounded-full',
        lg: 'h-11 px-6 text-[15px] rounded-full',
        icon: 'h-9 w-9 p-0 rounded-full',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
)

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button'
    return (
      <Comp ref={ref} className={cn(buttonVariants({ variant, size }), className)} {...props} />
    )
  },
)
Button.displayName = 'Button'

export { buttonVariants }
